import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import axios from 'axios'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import useDebounce from '../../helper/useDebounce'
import useSrcScript from '../../helper/useSrcScript'
import googleSrc from '../../scripts/googlePlaces'
import { sendAnalyticsEvents } from '../../analytics/common'
import { EVENTS } from '../../analytics/events'
import { selectPlace } from './familySlice'
import { fontSize, wh, tabletMinWidth, flexButton } from '../../mixins'
import UIButton from '../../UI/UIButton'
import { selectEventsData } from '../events/eventsSlice'
import PlaceAddedPopup from './PlaceAddedPopup'
import AddressAutocomplete from './AddressAutocomplete'


const { REACT_APP_LOCATIONIQ_TOKEN } = process.env;
const isArabic = navigator.language.startsWith('ar');
const textAlign = isArabic ? 'right' : 'left';

const SkipButton = styled.div`
  margin-top: 24px;
  ${fontSize(14)};
  line-height: 16px;
  color: #7D7D7D;
  text-align: center;
  
  @media screen and (max-width: ${tabletMinWidth}px) {
    text-align: right;
    position: absolute;
    top: 0;
    left: 0;
    padding: 0 24px;
    margin-top: 8px;
  }
`;

const FieldsWrapper = styled.div`
  @media screen and (max-width: ${tabletMinWidth}px) {
    margin-top: 34px;
  }
`;

const FakeInput = styled.div`
  display: flex;
  flex-direction: ${isArabic ? 'row-reverse' : 'row'};
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  ${wh('100%','56px')};
  background: #ffffff;
  border: 1px solid ${({error}) => error ? '#ff5b5b' : '#dee0e6'};
  border-radius: 12px;
  padding: 15px 16px;
  ${fontSize(16)};
  line-height: 19px;
  color: #494e57;
  text-align: left;
  position: relative;
    
  &:not(:last-child) {
    margin-bottom: 8px;
  }
    
  &:before {
    content: ${({iconType}) => iconType ? `url('../assets/${iconType}.svg')` : ''};
    ${wh('24px')};
    margin-right: 8px;
    filter: grayscale(1);
  }
    
  & > input {
    ${fontSize(16)};
    line-height: 19px;
    color: #494e57;
    text-align: ${textAlign};
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    border: none;
    outline: none;
    flex-grow: 1;
  }
`;

const MapWrapper = styled.div`
  position: relative;
  flex-grow: 1;
  max-height: 445px;
  margin: 24px 0;
`;

const MapContainer = styled.div`
  ${wh('100%')};
  position: absolute;
  top: 0;
  left: 0;
  overflow: hidden;
  border: 1px solid #FFA361;
  border-radius: 48px;
  isolation: isolate;
  
  div#map {
    ${wh('100%')};
    filter: grayscale(1);
    border-radius: inherit;
  }
`;

const MapScale = styled.div`
  ${flexButton('100%', '32px')};
  max-width: 327px;
  background: #d4d4d4;
  border-radius: 9px;
  padding: 2px;
`;

const ScaleItem = styled.div`
  ${wh('67px', '100%')};
  border-radius: 7px;
  font-weight: 500;
  ${fontSize(13)};
  line-height: 28px;
  letter-spacing: -0.08px;
  color: #323232;
  text-align: center;
  cursor: pointer;
  position: relative;
  ${({ active }) => active && `
    background: #FFFFFF;
    border: 0.5px solid rgba(0,0,0,.04);
    box-shadow: 0 3px 8px rgba(0,0,0,.12), 0 3px 1px rgba(0,0,0,.04);
    line-height: 26px;
  `}
    
  &:not(:last-child) {
    ${({ active, nextToActive }) => !(active || nextToActive) && `
      &:after {
        content: '';
        position: absolute;
        top: 50%;
        right: 0;
        margin-top: -8px;
        ${wh('1px', '16px')};
        background-color: #ADADAD;
        border-radius: 0.5px;
      }
    `}
  }
`;

const Marker = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  ${wh('120px')};
  background: rgba(255,163,97,.1);
  border-radius: 50%;
  pointer-events: none;
  z-index: 2;
    
  &:before,
  &:after {
    content: '';
    display: block;
    border-radius: inherit;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
    
  &:before {
    background: #FFFFFF;
    box-shadow: 0 0 4px rgba(0,0,0,.1);
    ${wh('48px')};
  }
    
  &:after {
    ${wh('24px')};
    background: url(../assets/pin.svg) center no-repeat;
  }
`;

const Buttons = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  margin-top: 10px;
  max-width: 327px;
`;

const LocationButton = styled.div`
  ${flexButton('56px')};
  flex: 1 0 56px;
  background: #FFE5D2;
  border-radius: 12px;
  margin-left: 12px;
`;

const AddPlace = ({ history }) => {
  const { t } = useTranslation();
  const status = useSrcScript(googleSrc);
  const place = useSelector(selectPlace);
  const eventsData = useSelector(selectEventsData);
  const [address, setAddress] = useState(t('add_place'));
  const [isValid, setIsValid] = useState(false);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [activeScale, setActiveScale] = useState(0);
  const [coordinates, setCoordinates] = useState({ lat: -33.867, lng: 151.195 });
  const debouncedCoordinates = useDebounce(coordinates, 800);
  const [map, setMap] = useState(null);
  const [isUSA, setUSA] = useState(false);
  const SCALES = isUSA
    ? [`500${t('ft')}`, `1500${t('ft')}`, `3000${t('ft')}`, `1${t('mi')}`, `3${t('mi')}`]
    : [`150${t('m')}`, `500${t('m')}`, `1${t('km')}`, `2${t('km')}`, `5${t('km')}`];

  useEffect(() => getCurrentPosition(), []);

  useEffect(() => {
    setIsValid(address && address.length > 3);
  }, [address]);

  useEffect(() => {
    debouncedCoordinates && getAddress(debouncedCoordinates).then((address) => setAddress(address))
  }, [debouncedCoordinates]);

  useEffect(() => {
    map && map.setCenter(debouncedCoordinates)
  }, [debouncedCoordinates, map]);

  useEffect(() => {
    if (status === 'ready') {
      let lat = -33.867,
        lng = 151.195,
        initPlace = new window.google.maps.LatLng(lat, lng),
        mapObj = new window.google.maps.Map(document.getElementById('map'), {
          center: initPlace,
          zoom: 18,
          disableDefaultUI: true,
          clickableIcons: false,
        });

      setMap(mapObj)
    }
  }, [status]);

  useEffect(() => {
    if (!map) return;
    map.addListener('dragend', () => {
      const { lat, lng } = map.getCenter();
      const coords = { lat: lat(), lng: lng() };

      setCoordinates((prevState) => {
        if (prevState !== coords) return coords
      })
    })
  }, [map]);

  useEffect(() => sendAnalyticsEvents(EVENTS.onboardingAddPlace, eventsData), [eventsData]);

  const getCurrentPosition = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((position) => {
      const coords = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      };
      setCoordinates((prevState) => {
        if (prevState !== coords) return coords
      })
    })
  };

  const getAddress = async (coords) => {
    if (!REACT_APP_LOCATIONIQ_TOKEN) return;
    const address = null;
    const { lat, lng } = coords;
    const url = `https://eu1.locationiq.com/v1/reverse.php?key=${REACT_APP_LOCATIONIQ_TOKEN}&lat=${lat}&lon=${lng}&format=json`;
    const {data, error} = await axios.get(url);

    if (error) return address;

    setUSA(data?.address?.country_code === 'us');

    return data?.address?.road
      ? `${data.address.road}, ${data.address.house_number || data.address.neighbourhood || ''}`
      : data.address.suburb || data.address.state || address
  };

  const onAddPlaceClick = () => {
    sendAnalyticsEvents(EVENTS.onboardingAddPlaceSubmitted, eventsData);
    setShowAddPopup(true)
  };

  const renderScales = (sc, i) => {
    return (
      <ScaleItem key={sc}
                 active={i === activeScale}
                 nextToActive={activeScale - i === 1}
                 onClick={() => setZoom(i)}
      >
        {sc}
      </ScaleItem>
    )
  };

  const setZoom = (i) => {
    map.setZoom(18 - i);
    setActiveScale(i)
  };

  const onSkipClick = () => {
    sendAnalyticsEvents(EVENTS.onboardingAddPlaceSkip, eventsData);
    history.push('/signup')
  };

  return (
    <>
      <FieldsWrapper>
        <FakeInput iconType={'bookmark'} >
          <span>{t(place) || t('custom')}</span>
        </FakeInput>
        <FakeInput iconType={'pin'} error={!isValid} >
          <input type='text' value={address} onChange={(e) => setAddress(e.target.value)} onFocus={() => setShowAutocomplete(true)} />
          {(status === 'ready') && (
            <AddressAutocomplete show={showAutocomplete && !!address}
                                 map={map}
                                 address={address}
                                 setCoordinates={setCoordinates}
                                 setShowAutocomplete={setShowAutocomplete}
                                 setAddress={setAddress}
            />
          )}
        </FakeInput>
      </FieldsWrapper>
      <MapWrapper>
        <MapContainer>
          <div id="map" />
          <Marker />
        </MapContainer>
      </MapWrapper>
      <MapScale>{SCALES.map(renderScales)}</MapScale>
      <Buttons>
        <UIButton onClick={onAddPlaceClick} disabled={!isValid} customId={'add-place-button'} customText={t('add_place')} />
        <LocationButton onClick={getCurrentPosition}>
          <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
            <path fillRule='evenodd' clipRule='evenodd' d='M13.667 22C13.646 22 13.626 21.999 13.606 21.998C13.154 21.971 12.777 21.643 12.687 21.199L11.149 13.632C11.069 13.238 10.762 12.931 10.368 12.851L2.80098 11.312C2.35698 11.223 2.02898 10.846 2.00198 10.394C1.97498 9.94103 2.25398 9.52703 2.68398 9.38503L18.684 4.05203C19.043 3.93003 19.439 4.02403 19.707 4.29303C19.975 4.56103 20.068 4.95703 19.949 5.31603L14.615 21.316C14.479 21.727 14.095 22 13.667 22'
                  fill='#F58A3C'/>
          </svg>
        </LocationButton>
      </Buttons>
      <SkipButton><span onClick={onSkipClick} id='skip-map-button'>{t('skip')}</span></SkipButton>
      {showAddPopup && <PlaceAddedPopup history={history} />}
    </>
  )
};

export default AddPlace
